import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import jwt_decode from 'jwt-decode';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiBaseRequest } from 'src/app/model/api/api-request/api-base-request';
import { StorageService } from 'src/app/services';
import { AuthorizationService } from 'src/app/services/authorization/authorization.service';
import { StorageKeys } from '../../model/enums/storage-keys';
import { JWT } from '../../model/interfaces/json-web-token';

declare type iJwt = {
    aud: string
    exp: number
    iss: string
    jti: string
    sub: string
};

@Injectable()
export class AuthInterceptor implements HttpInterceptor
{
    private readonly injector: Injector;
    private readonly storageService: StorageService;
    
    // Cached AuthorizationService instance once retrieved
    private authorizationService: AuthorizationService | null = null;

    public constructor(injector: Injector, storageService: StorageService)
    {
        this.injector = injector;
        this.storageService = storageService;
    }

    /**
     * Lazily retrieves and caches the AuthorizationService instance.
     * 
     * The service is retrieved on first use and cached to avoid repeated injector lookups,
     * ensuring we reuse the singleton instance provided by Angular's DI.
     * 
     * We use Injector instead of constructor injection to avoid a circular dependency. Otherwise, we would
     * have to rethink the service structure or use alternative patterns.
     */
    private getAuthorizationService(): AuthorizationService
    {
        if (!this.authorizationService) {
            this.authorizationService = this.injector.get(AuthorizationService);
        }
        return this.authorizationService;
    }

    public intercept(request: HttpRequest<ApiBaseRequest>, next: HttpHandler): Observable<HttpEvent<any>>
    {
        const token = this.storageService.getItem(StorageKeys.JWT);

        if (token)
        {
            const decodedToken: iJwt = jwt_decode(token);
            const cloned = request.clone({
                headers: request.headers
                    .set('Authorization', `Bearer ${token}`)
                    .set('Playerid', `${decodedToken.sub}`) // sub is user id in JWT
            });

            return next.handle(cloned).pipe(
                map(event => {
                    if (event instanceof HttpResponse) {
                        const strRefreshToken = event.headers.get('refresh-token');
                        if (strRefreshToken != null) {
                            const jwt: JWT = JSON.parse(strRefreshToken);
                            this.getAuthorizationService().updateToken(jwt);
                        }
                    }
                    
                    // For testing: uncomment to simulate an error
                    // if (request.url.includes('/test-error')) {
                    //     throw new HttpErrorResponse({
                    //         error: 'Simulated 401 error',
                    //         status: 401,
                    //         statusText: 'Unauthorized'
                    //     });
                    // }
                    return event;
                }),
                catchError(error => {
                    if (error instanceof HttpErrorResponse) {
                        if (error.status === 401 || error.status === 403) {
                            this.storageService.removeItem(StorageKeys.JWT);
                        }
                    }
                    return throwError(() => error);
                })
            );
        }
        else
        {
            return next.handle(request);
        }
    }
}

